/**
 * LocalStorage Utilities
 * Provides type-safe access to localStorage with error handling
 */

const STORAGE_KEYS = {
  AUTH_TOKEN: 'crm_auth_token',
  USER_EMAIL: 'crm_user_email',
  TENANT_ID: 'crm_tenant_id',
  TENANT_SLUG: 'crm_tenant_slug',
  REMEMBER_ME: 'crm_remember_me',
  MUST_CHANGE_PASSWORD: 'crm_must_change_password',
  MFA_ENROLLMENT_REQUIRED: 'crm_mfa_enrollment_required',
} as const;

/**
 * Persist the MFA enrollment gate (tracker item 63). Like
 * mustChangePassword, the JWT carries no such claim.
 */
export function saveMfaEnrollmentRequired(required: boolean): void {
  try {
    if (required) {
      localStorage.setItem(STORAGE_KEYS.MFA_ENROLLMENT_REQUIRED, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.MFA_ENROLLMENT_REQUIRED);
    }
  } catch (error) {
    console.error('Failed to save MFA enrollment flag:', error);
  }
}

export function getMfaEnrollmentRequired(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.MFA_ENROLLMENT_REQUIRED) === 'true';
  } catch (error) {
    console.error('Failed to get MFA enrollment flag:', error);
    return false;
  }
}

/**
 * Persist the must-change-password flag. The JWT carries no such claim, so
 * without this a page reload would lose the gate until the next 403.
 */
export function saveMustChangePassword(mustChange: boolean): void {
  try {
    if (mustChange) {
      localStorage.setItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD);
    }
  } catch (error) {
    console.error('Failed to save must-change-password flag:', error);
  }
}

/**
 * Get the persisted must-change-password flag
 */
export function getMustChangePassword(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD) === 'true';
  } catch (error) {
    console.error('Failed to get must-change-password flag:', error);
    return false;
  }
}

/**
 * Save authentication token
 */
export function saveAuthToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  } catch (error) {
    console.error('Failed to save auth token:', error);
  }
}

/**
 * Get authentication token
 */
export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Clear authentication token
 */
export function clearAuthToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Failed to clear auth token:', error);
  }
}

/**
 * Save user email
 */
export function saveUserEmail(email: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email);
  } catch (error) {
    console.error('Failed to save user email:', error);
  }
}

/**
 * Get user email
 */
export function getUserEmail(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  } catch (error) {
    console.error('Failed to get user email:', error);
    return null;
  }
}

/**
 * Save tenant ID
 */
export function saveTenantId(tenantId: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.TENANT_ID, tenantId);
  } catch (error) {
    console.error('Failed to save tenant ID:', error);
  }
}

/**
 * Get tenant ID
 */
export function getTenantId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.TENANT_ID);
  } catch (error) {
    console.error('Failed to get tenant ID:', error);
    return null;
  }
}

/**
 * Save tenant slug
 */
export function saveTenantSlug(slug: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.TENANT_SLUG, slug);
  } catch (error) {
    console.error('Failed to save tenant slug:', error);
  }
}

/**
 * Get tenant slug
 */
export function getTenantSlug(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.TENANT_SLUG);
  } catch (error) {
    console.error('Failed to get tenant slug:', error);
    return null;
  }
}

/**
 * Set remember me preference
 */
export function setRememberMe(remember: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, remember ? 'true' : 'false');
  } catch (error) {
    console.error('Failed to set remember me:', error);
  }
}

/**
 * Get remember me preference
 */
export function getRememberMe(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
  } catch (error) {
    console.error('Failed to get remember me:', error);
    return false;
  }
}

/**
 * Clear all auth-related storage
 */
export function clearAuthStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
    localStorage.removeItem(STORAGE_KEYS.TENANT_ID);
    localStorage.removeItem(STORAGE_KEYS.TENANT_SLUG);
    localStorage.removeItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD);
    localStorage.removeItem(STORAGE_KEYS.MFA_ENROLLMENT_REQUIRED);
  } catch (error) {
    console.error('Failed to clear auth storage:', error);
  }
}
